import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import {
  IconAdjustmentsHorizontal,
  IconAlertCircle,
  IconBulb,
  IconExternalLink,
  IconShieldCheck,
  IconSparkles,
  IconTable,
} from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Shema } from '../api/odjemalec';
import { barve } from '../tema';
import { sprememba, stanjeZahteve, vrednost, type Sporocilo } from './asistent';
import { GrafKartice } from './GrafKartice';
import { GumbKomentarja, Komentar } from './KomentarKartice';
import { ParametriKartice } from './ParametriKartice';

type Kartica = Shema['AsistentKartica'];
type Zmoznost = Shema['AsistentZmoznost'];

/**
 * Rezultat orodja asistenta, kot ga pripravi strežnik (model ga ne vidi): podatki poslovanja (tabela), seznam
 * zmožnosti, pravice uporabnika, zahteva, ki je asistent še ne zna, ali napaka. Neznano vrsto (novejši strežnik) pokaže z naslovom in opisom. {@code vprasaj} zastavi vzorčno
 * vprašanje.
 */
export function KarticaAsistenta({
  kartica,
  vprasaj,
  sporociloId,
  naSpremembo,
}: {
  kartica: Kartica;
  vprasaj?: (v: string) => void;
  /** Sporočilo s kartico (za spremembo parametrov in komentar); brez = samo prikaz. */
  sporociloId?: number;
  naSpremembo?: (s: Sporocilo) => void;
}) {
  switch (kartica.vrsta) {
    case 'ZMOZNOSTI':
      return (
        <Okvir>
          <Group gap={8} mb={4} wrap="nowrap">
            <IconSparkles size={18} color={barve.primarna} style={{ flexShrink: 0 }} />
            <Text fw={700}>{kartica.naslov}</Text>
          </Group>
          {kartica.opis && (
            <Text size="sm" c="dimmed" mb="sm">
              {kartica.opis}
            </Text>
          )}
          <SeznamZmoznosti zmoznosti={kartica.zmoznosti ?? []} vprasaj={vprasaj} />
          <Opombe opombe={kartica.opombe} />
        </Okvir>
      );
    case 'TABELA':
      return <TabelaAsistenta kartica={kartica} sporociloId={sporociloId} naSpremembo={naSpremembo} />;
    case 'PRAVICE':
      return <PraviceAsistenta kartica={kartica} />;
    case 'NI_PODPRTO': {
      const s = stanjeZahteve(kartica.stanjeZahteve);
      return (
        <Okvir>
          <Group gap={8} mb={4} justify="space-between" wrap="nowrap">
            <Group gap={8} wrap="nowrap">
              <IconBulb size={18} color={barve.primarna} style={{ flexShrink: 0 }} />
              <Text fw={700}>{kartica.naslov}</Text>
            </Group>
            {kartica.zahtevaId && (
              <Badge color={s.barva} variant="light">
                {s.naziv}
              </Badge>
            )}
          </Group>
          <Text size="sm">{kartica.opis}</Text>
          {kartica.povratnaInformacijaId && (
            <Anchor component={Link} to={`/povratne-informacije/${kartica.povratnaInformacijaId}`} size="sm">
              Odpri povratno informacijo
            </Anchor>
          )}
        </Okvir>
      );
    }
    case 'NAPAKA':
      return (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} title={kartica.naslov}>
          {kartica.opis}
        </Alert>
      );
    default:
      return (
        <Okvir>
          <Text fw={700}>{kartica.naslov}</Text>
          {kartica.opis && <Text size="sm">{kartica.opis}</Text>}
        </Okvir>
      );
  }
}

/** Barva ravni pravice (naziv s strežnika: Brez / Pregled / Urejanje). */
const BARVA_RAVNI: Record<string, string> = { Brez: 'gray', Pregled: 'blue', Urejanje: 'green' };

/**
 * Vloga uporabnika in njegove pravice po področjih (vprašano področje poudarjeno), kdo jih lahko spremeni, povezava na
 * navodila ali – kdor ureja vloge – na Nastavitve → Uporabniki. Tudi namesto podatkov, ko uporabnik pravice nima.
 */
function PraviceAsistenta({ kartica }: { kartica: Kartica }) {
  const vrstice = kartica.vrstice ?? [];
  return (
    <Okvir>
      <Group gap={8} mb={4} wrap="nowrap">
        <IconShieldCheck size={18} color={barve.primarna} style={{ flexShrink: 0 }} />
        <Text fw={700}>{kartica.naslov}</Text>
      </Group>
      {kartica.opis && (
        <Text size="sm" mb="xs">
          {kartica.opis}
        </Text>
      )}
      <Table.ScrollContainer minWidth={420}>
        <Table verticalSpacing={4} fz="sm">
          <Table.Thead>
            <Table.Tr>
              {(kartica.stolpci ?? []).map((c) => (
                <Table.Th key={c.kljuc}>{c.naziv}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {vrstice.map((r) => {
              const raven = String(r.raven ?? '');
              return (
                <Table.Tr
                  key={String(r.kljuc)}
                  style={{ background: r.poudarek ? barve.izbrano : undefined }}
                  aria-current={r.poudarek ? 'true' : undefined}
                >
                  <Table.Td fw={r.poudarek ? 700 : 600}>{String(r.podrocje ?? '')}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>
                    <Badge
                      size="sm"
                      variant="light"
                      color={BARVA_RAVNI[raven] ?? 'gray'}
                      tt="none"
                      style={{ overflow: 'visible' }}
                      styles={{ label: { overflow: 'visible' } }}
                    >
                      {raven}
                    </Badge>
                  </Table.Td>
                  <Table.Td c="dimmed">{String(r.opis ?? '')}</Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Opombe opombe={kartica.opombe} />
      <Group gap="md" mt="xs">
        <Anchor component={Link} to="/pomoc/vloge-in-pravice" size="sm">
          Navodila: vloge in pravice
        </Anchor>
        {kartica.povezava === '/nastavitve/vloge' && (
          <Anchor component={Link} to="/nastavitve/vloge" size="sm">
            <IconExternalLink size={12} style={{ verticalAlign: -1 }} /> Uredi vloge
          </Anchor>
        )}
      </Group>
    </Okvir>
  );
}

/** Zmožnosti po področjih; vzorčna vprašanja so gumbi, ki vprašanje takoj zastavijo. */
export function SeznamZmoznosti({ zmoznosti, vprasaj }: { zmoznosti: Zmoznost[]; vprasaj?: (v: string) => void }) {
  const podrocja = [...new Set(zmoznosti.map((z) => z.podrocje ?? ''))];
  return (
    <Stack gap="sm">
      {podrocja.map((p) => (
        <Stack key={p} gap={6}>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
            {p}
          </Text>
          {zmoznosti
            .filter((z) => (z.podrocje ?? '') === p)
            .map((z) => (
              <Box key={z.orodje}>
                <Text size="sm" fw={600}>
                  {z.naziv}
                </Text>
                <Text size="sm" c="dimmed">
                  {z.opis}
                </Text>
                <Group gap={6} mt={4}>
                  {(z.primeri ?? []).map((primer) =>
                    vprasaj ? (
                      <Button
                        key={primer}
                        size="compact-sm"
                        variant="light"
                        radius="xl"
                        onClick={() => vprasaj(primer)}
                        styles={{ label: { whiteSpace: 'normal', textAlign: 'left' } }}
                        h="auto"
                        py={4}
                      >
                        {primer}
                      </Button>
                    ) : (
                      <Text key={primer} size="sm" fs="italic">
                        »{primer}«
                      </Text>
                    ),
                  )}
                </Group>
              </Box>
            ))}
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * Podatki poslovanja: naslov s povezavo na zaslon v aplikaciji, uporabljeni filtri (da uporabnik preveri, ali ga je
 * asistent razumel), zbirne vrednosti (s primerjavo), tabela (vrstica s povezavo odpre npr. vozilo) in pojasnila.
 */
function TabelaAsistenta({
  kartica,
  sporociloId,
  naSpremembo,
}: {
  kartica: Kartica;
  sporociloId?: number;
  naSpremembo?: (s: Sporocilo) => void;
}) {
  const [pogled, setPogled] = useState<'tabela' | 'graf'>('tabela');
  const [urejanje, setUrejanje] = useState(false);
  const stolpci = kartica.stolpci ?? [];
  const vrstice = kartica.vrstice ?? [];
  const grafMogoc = !!kartica.graf && vrstice.length >= 2;
  const dejavno = sporociloId !== undefined && naSpremembo !== undefined;
  return (
    <Okvir>
      <Group gap={8} mb={4} justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
          <IconTable size={18} color={barve.primarna} style={{ flexShrink: 0 }} />
          <Text fw={700}>{kartica.naslov}</Text>
        </Group>
        {kartica.povezava && (
          <Anchor component={Link} to={kartica.povezava} size="xs" style={{ flexShrink: 0 }}>
            <IconExternalLink size={12} style={{ verticalAlign: -1 }} /> Odpri v aplikaciji
          </Anchor>
        )}
      </Group>
      {(kartica.filtri ?? []).length > 0 && (
        <Group gap={4} mb="xs" aria-label="Filtri">
          {(kartica.filtri ?? []).map((f) => (
            <Badge key={f} size="sm" variant="light" color="gray" tt="none" fw={500}>
              {f}
            </Badge>
          ))}
          {kartica.spremenjeno && (
            <Badge size="sm" variant="light" color="yellow" tt="none" fw={500}>
              prilagojeno
            </Badge>
          )}
        </Group>
      )}
      {(grafMogoc || (dejavno && ((kartica.polja ?? []).length > 0 || !kartica.komentar))) && (
        <Group gap="xs" mb="xs" justify="space-between" wrap="wrap">
          {grafMogoc ? (
            <SegmentedControl
              size="xs"
              aria-label="Pogled"
              value={pogled}
              onChange={(v) => setPogled(v as 'tabela' | 'graf')}
              data={[
                { value: 'tabela', label: 'Tabela' },
                { value: 'graf', label: 'Graf' },
              ]}
            />
          ) : (
            <span />
          )}
          {dejavno && (
            <Group gap={6}>
              {(kartica.polja ?? []).length > 0 && (
                <Button
                  size="compact-xs"
                  variant="light"
                  leftSection={<IconAdjustmentsHorizontal size={14} />}
                  onClick={() => setUrejanje(true)}
                >
                  Prilagodi
                </Button>
              )}
              {!kartica.komentar && <GumbKomentarja sporociloId={sporociloId} naSpremembo={naSpremembo} />}
            </Group>
          )}
        </Group>
      )}
      {(kartica.povzetek ?? []).length > 0 && (
        <SimpleGrid cols={{ base: 2, xs: 3 }} spacing="xs" verticalSpacing="xs" mb="sm">
          {(kartica.povzetek ?? []).map((v) => (
            <Box key={v.naziv}>
              <Text size="xs" c="dimmed">
                {v.naziv}
              </Text>
              <Text fw={700}>{vrednost(v.vrednost, v.vrsta)}</Text>
              {v.primerjava !== undefined && v.primerjava !== null && (
                <Text size="xs" c="dimmed">
                  prej {vrednost(v.primerjava, v.vrsta)}
                  {v.sprememba !== undefined && v.sprememba !== null && ` · ${sprememba(v.sprememba)}`}
                </Text>
              )}
            </Box>
          ))}
        </SimpleGrid>
      )}
      {pogled === 'graf' && grafMogoc ? (
        <GrafKartice kartica={kartica} />
      ) : vrstice.length === 0 ? (
        <Text size="sm" c="dimmed">
          Ni podatkov za izbrano obdobje in filtre.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={Math.max(320, stolpci.length * 110)} type="native">
          <Table striped verticalSpacing={4} horizontalSpacing={6} fz="xs">
            <Table.Thead>
              <Table.Tr>
                {stolpci.map((c) => (
                  <Table.Th key={c.kljuc} ta={c.vrsta === 'BESEDILO' || c.vrsta === 'DATUM' ? undefined : 'right'}>
                    {c.naziv}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {vrstice.map((r, i) => (
                <Table.Tr key={i}>
                  {stolpci.map((c, j) => {
                    const besedilo = vrednost(r[c.kljuc ?? ''], c.vrsta);
                    const povezava = j === 0 && typeof r.povezava === 'string' ? r.povezava : null;
                    return (
                      <Table.Td
                        key={c.kljuc}
                        ta={c.vrsta === 'BESEDILO' || c.vrsta === 'DATUM' ? undefined : 'right'}
                        className={c.vrsta === 'BESEDILO' ? undefined : 'brez-preloma'}
                      >
                        {povezava ? (
                          <Anchor component={Link} to={povezava} size="xs">
                            {besedilo}
                          </Anchor>
                        ) : (
                          besedilo
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Opombe opombe={kartica.opombe} />
      {kartica.komentar && <Komentar besedilo={kartica.komentar} zaracunano={kartica.komentarZaracunano} />}
      {urejanje && dejavno && (
        <ParametriKartice
          sporociloId={sporociloId}
          polja={kartica.polja ?? []}
          spremenjeno={!!kartica.spremenjeno}
          zapri={() => setUrejanje(false)}
          naSpremembo={naSpremembo}
        />
      )}
    </Okvir>
  );
}

function Opombe({ opombe }: { opombe?: string[] }) {
  if (!opombe?.length) {
    return null;
  }
  return (
    <Stack gap={2} mt="xs">
      {opombe.map((o) => (
        <Text key={o} size="xs" c="dimmed">
          {o}
        </Text>
      ))}
    </Stack>
  );
}

function Okvir({ children }: { children: ReactNode }) {
  return (
    <Paper withBorder radius="md" px="md" py="sm" style={{ overflowWrap: 'anywhere' }}>
      {children}
    </Paper>
  );
}
